import { useState, type ReactNode } from "react";
import { AttachmentsPanel } from "@/modules/attachments/components/AttachmentsPanel";
import { CommentsPanel } from "@/modules/comments/components/CommentsPanel";
import { HistoryPanel } from "@/modules/audit/components/HistoryPanel";
import type { AttachmentDocType } from "@/shared/api/attachments";
import type { CommentDocType } from "@/shared/api/comments";
import { cn } from "@/lib/utils";

type TabId = "comments" | "files" | "history";

/**
 * Unified Activity family on docs — plan/35.
 * Comments primary; Files / History share the same chrome.
 */
export function DocActivity(props: {
  companySlug: string;
  docType: CommentDocType;
  docId: string;
  linkPath?: string;
  /** When true, Files tab uses docType as AttachmentDocType */
  showFiles?: boolean;
  className?: string;
}) {
  const showFiles = props.showFiles !== false;
  const [tab, setTab] = useState<TabId>("comments");

  const tabs: { id: TabId; label: string }[] = [
    { id: "comments", label: "Comments" },
    ...(showFiles ? [{ id: "files" as const, label: "Files" }] : []),
    { id: "history", label: "History" },
  ];

  let body: ReactNode = null;
  if (tab === "comments") {
    body = (
      <CommentsPanel
        companySlug={props.companySlug}
        docType={props.docType}
        docId={props.docId}
        linkPath={props.linkPath}
        embedded
      />
    );
  } else if (tab === "files" && showFiles) {
    body = (
      <AttachmentsPanel
        companySlug={props.companySlug}
        docType={props.docType as AttachmentDocType}
        docId={props.docId}
        embedded
      />
    );
  } else {
    body = (
      <HistoryPanel
        companySlug={props.companySlug}
        docType={props.docType}
        docId={props.docId}
        embedded
      />
    );
  }

  return (
    <section
      className={cn(
        "mt-10 overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)] shadow-[var(--shadow-soft)]",
        props.className,
      )}
    >
      <div className="flex items-end gap-1 border-b border-[var(--color-border)]/90 px-3 pt-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={cn(
              "border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
              tab === t.id
                ? "border-[var(--color-foreground)] text-[var(--color-foreground)]"
                : "border-transparent text-[var(--color-muted)] hover:text-[var(--color-foreground)]",
            )}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="px-4 py-4 sm:px-5">{body}</div>
    </section>
  );
}
